import { BaseProvider } from './BaseProvider';
import { ProviderType, ProviderModel, GenerateRequest, GenerateResponse, PROVIDER_DEFAULTS } from './types';

export class GroqProvider extends BaseProvider {
  readonly type: ProviderType = 'groq';
  readonly name = 'Groq';
  readonly supportsStreaming = true;
  readonly supportsFunctionCalling = true;
  readonly requiresApiKey = true;

  private getBaseUrl(): string {
    return this.config?.baseUrl || PROVIDER_DEFAULTS.groq.baseUrl || 'https://api.groq.com/openai/v1';
  }

  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.config?.apiKey || ''}`
    };
  }

  async testConnection(): Promise<{ success: boolean; message: string; models?: ProviderModel[] }> {
    if (!this.config?.apiKey) {
      return { success: false, message: 'Groq API key is required' };
    }

    try {
      const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/models`, {
        method: 'GET',
        headers: this.getHeaders()
      }, 10000);

      if (!response.ok) {
        if (response.status === 401) {
          return { success: false, message: 'Invalid Groq API key' };
        }
        return { success: false, message: `Groq API returned ${response.status}` };
      }

      const data = await response.json();
      const models = (data.data || [])
        .filter((m: any) => m.id && !m.id.includes('whisper'))
        .map((m: any) => ({
          id: m.id,
          name: m.id,
          contextLength: m.context_window,
          description: m.owned_by || 'Groq'
        }));

      return {
        success: true,
        message: `Connected to Groq. ${models.length} models available.`,
        models
      };
    } catch (error: any) {
      return { success: false, message: `Cannot connect to Groq: ${this.formatError(error)}` };
    }
  }

  async listModels(): Promise<ProviderModel[]> {
    if (!this.config?.apiKey) {
      return PROVIDER_DEFAULTS.groq.defaultModels.map(id => ({ id, name: id }));
    }

    try {
      const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/models`, {
        method: 'GET',
        headers: this.getHeaders()
      }, 10000);

      if (!response.ok) {
        return PROVIDER_DEFAULTS.groq.defaultModels.map(id => ({ id, name: id }));
      }

      const data = await response.json();
      return (data.data || [])
        .filter((m: any) => m.id && !m.id.includes('whisper'))
        .map((m: any) => ({
          id: m.id,
          name: m.id,
          contextLength: m.context_window,
          description: m.owned_by || 'Groq'
        }));
    } catch {
      return PROVIDER_DEFAULTS.groq.defaultModels.map(id => ({ id, name: id }));
    }
  }

  async generate(request: GenerateRequest): Promise<GenerateResponse> {
    const model = this.config?.model || PROVIDER_DEFAULTS.groq.model;
    
    const messages = [];
    
    if (request.systemPrompt) {
      messages.push({ role: 'system', content: request.systemPrompt });
    }
    
    messages.push(...request.messages.map(m => ({
      role: m.role,
      content: m.content
    })));

    const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/chat/completions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        model,
        messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.maxTokens ?? 4096,
        stream: false
      })
    }, 60000);

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Groq error: ${error}`);
    }

    const data = await response.json();
    const choice = data.choices?.[0];
    
    return {
      text: choice?.message?.content || '',
      model: data.model || model!,
      finishReason: choice?.finish_reason || 'stop',
      usage: data.usage ? {
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
        totalTokens: data.usage.total_tokens
      } : undefined
    };
  }

  async *generateStream(request: GenerateRequest): AsyncGenerator<string, void, unknown> {
    const model = this.config?.model || PROVIDER_DEFAULTS.groq.model;
    
    const messages = [];
    
    if (request.systemPrompt) {
      messages.push({ role: 'system', content: request.systemPrompt });
    }
    
    messages.push(...request.messages.map(m => ({
      role: m.role,
      content: m.content
    })));

    const response = await fetch(`${this.getBaseUrl()}/chat/completions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        model,
        messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.maxTokens ?? 4096,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`Groq error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;
        
        try {
          const parsed = JSON.parse(data);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) {
            yield content;
          }
        } catch {
          continue;
        }
      }
    }
  }
}
